import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import { SvgIcon } from '@mui/material'
import download from 'downloadjs/download'
import React from 'react'
import { ValueMessageLinkedData } from '../../../../../../../shared/queries/useGetValueMessageLinkedDataQuery'
import { useGetEvidenceFile } from '../../../../../../../shared/queries/useGetEvidenceFile'
import { ReactComponent as LinkIcon } from '../../../../../../../assets/images/hyperlink.svg'
import { ReactComponent as PencilIcon } from '../../../../../../../assets/images/pencil-write-2.svg'
import { ReactComponent as DownloadIcon } from '../../../../../../../assets/images/download-bottom.svg'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import Link from '@mui/material/Link'
import { ValueMessage } from '../../../../../../ValueNarrativesView/queries/useGetValueNarrativesQuery'
import { NO_VALUE_LABEL } from '../../../../../../../shared/utils/displayValue'

type SupportingEvidenceExpandedRowItemProps = {
    evidence: ValueMessageLinkedData['supportingEvidence'][number]
    keyEvidenceStatements: ValueMessageLinkedData['keyEvidenceStatements']
    navigationStack: ArrayLike<object>
    valueMessage: ValueMessage
}

const Bulletpoint = () => <li style={{ width: 0, marginTop: '5px', fontSize: '12px' }} />

const SupportingEvidenceExpandedRowItem = ({
    evidence,
    keyEvidenceStatements,
    navigationStack,
    valueMessage,
}: SupportingEvidenceExpandedRowItemProps) => {
    const { refetch: getEvidenceFile } = useGetEvidenceFile(evidence._id)

    const history = useHistory()

    const handleDownloadEvidenceFile = async () => {
        const { data: evidenceFile } = await getEvidenceFile()
        await download(evidenceFile, evidence.originalFileName, evidenceFile.type)
    }

    const addToStack = () => {
        let arr = Array.from(navigationStack)
        arr.push({
            isAutoOpenPanel: false,
            path: history.location.pathname,
            data: valueMessage,
        })
        return arr
    }

    const renderItem = ({ id, title, showLinks }: { id: string; title: string; showLinks: boolean }) => {
        const trimmedCitation = (evidence?.shortCitation?.trim() || evidence?.citation?.trim()) ?? ''
        const cleanedCitation = trimmedCitation.endsWith('.') ? trimmedCitation.slice(0, -1) : trimmedCitation

        return (
            <Stack key={id} direction="row" gap={2}>
                <Bulletpoint />

                <Typography
                    flex={6}
                    variant="16x400"
                    color="figma.grayscale.textHier2"
                    lineHeight="26px"
                    style={{ whiteSpace: 'break-spaces' }}
                >
                    {title}
                </Typography>

                {!showLinks && <Stack flex={4} />}
                {showLinks && (
                    <Stack flex={4} direction="row" gap={1} ml={2} alignItems="flex-start" justifyContent="flex-end">
                        <Tooltip
                            title="Open Evidence"
                            enterDelay={600}
                            enterNextDelay={600}
                            sx={{
                                flex: 1,
                                marginInline: 1,
                            }}
                        >
                            <Link
                                component={RouterLink}
                                underline="none"
                                color="secondary"
                                to={{
                                    pathname: '/evidence',
                                    autoOpenData: {
                                        isAutoOpenPanel: true,
                                        autoOpenData: evidence,
                                        previousDataStack: addToStack(),
                                    },
                                }}
                            >
                                <Typography
                                    variant="16x400"
                                    color="figma.grayscale.tier3"
                                    sx={{
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            color: 'figma.primary',
                                            cursor: 'pointer',
                                        },
                                    }}
                                    lineHeight="26px"
                                >
                                    {!!cleanedCitation ? (
                                        <>
                                            {cleanedCitation}
                                            <span style={{ whiteSpace: 'nowrap' }}>
                                                .
                                                <SvgIcon
                                                    component={PencilIcon}
                                                    fontSize="small"
                                                    color="primary"
                                                    inheritViewBox
                                                    sx={{
                                                        marginLeft: '6px',
                                                        fontSize: '14px',
                                                    }}
                                                />
                                            </span>
                                        </>
                                    ) : (
                                        NO_VALUE_LABEL
                                    )}
                                </Typography>
                            </Link>
                        </Tooltip>

                        {evidence.citation && (
                            <Tooltip title="Download File" enterDelay={600} enterNextDelay={600}>
                                <IconButton
                                    disabled={!evidence.originalFileName}
                                    size="small"
                                    onClick={handleDownloadEvidenceFile}
                                    sx={{
                                        color: (theme) =>
                                            !!evidence.originalFileName
                                                ? `figma.primary`
                                                : `${theme.palette.figma.grayscale.tier5} !important`,
                                        cursor: !!evidence.originalFileName ? 'pointer' : 'not-allowed !important',
                                    }}
                                >
                                    <SvgIcon
                                        component={DownloadIcon}
                                        fontSize="small"
                                        color="inherit"
                                        inheritViewBox
                                        sx={{
                                            fontSize: '16px',
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        {evidence.citation && (
                            <Tooltip title="Open Link" enterDelay={600} enterNextDelay={600} sx={{}}>
                                <IconButton
                                    disabled={!evidence.link}
                                    size="small"
                                    component="a"
                                    href={evidence.link}
                                    target="_blank"
                                    sx={{
                                        color: (theme) =>
                                            !!evidence.link
                                                ? `figma.primary`
                                                : `${theme.palette.figma.grayscale.tier5} !important`,
                                        cursor: !!evidence.link ? 'alias' : 'not-allowed !important',
                                    }}
                                >
                                    <SvgIcon
                                        component={LinkIcon}
                                        fontSize="small"
                                        color="inherit"
                                        inheritViewBox
                                        sx={{
                                            fontSize: '16px',
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Stack>
        )
    }

    if (!keyEvidenceStatements || keyEvidenceStatements.length === 0) {
        return renderItem({ id: evidence._id, title: evidence.name, showLinks: true })
    }

    return (
        <>
            {keyEvidenceStatements.map((kes, index) =>
                renderItem({ id: kes._id, title: kes.statement, showLinks: index === 0 })
            )}
        </>
    )
}

export default SupportingEvidenceExpandedRowItem
